<template>
  <section class="block rounded-lg mb-8 shadow bg-white w-full p-4 outline-none">
    <h1 class="text-3xl font-normal mb-4">Programs</h1>
    <h2 class="text-xl font-normal mt-8">
      Search Programs
    </h2>
    <BaseAccordion>
      <template #trigger="{ setIsOpen, isOpen }">
        <div class="search-wrapper flex mt-4">
          <SearchInput
            v-model="searchQuery"
            placeholder="Search for programs..."
            :is-loading="isLoading"
          />
          <BaseButton
            v-if="!hasDepartmentFilter"
            aria-label="Advanced filters toggle"
            title="Advanced filters toggle"
            :aria-expanded="isOpen ? 'true' : 'false'"
            :type="isOpen ? 'primary' : 'secondary'"
            class="ml-4 bg-color color-theme-base hover:opacity-80"
            @click="setIsOpen(!isOpen)"
          >
            <BaseIcon icon="filter" />
          </BaseButton>
        </div>
      </template>
      <template #content>
        <div class="bg-white mb-4 p-4 -mt-8">
          <span
            class="visually-hidden"
            role="status"
          >
            Advanced filters open
          </span>
          <h4 class="font-semibold pt-2">Advanced Search</h4>
          <DepartmentFilter v-model="departmentFilter" />
        </div>
      </template>
    </BaseAccordion>
    <h2 class="text-lg font-normal flex justify-between">
      Results
      <BasePagination
        v-if="total > pageSize"
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-change="updatePage"
      />
    </h2>
    <p role="status">
      <!-- This is commented out because the default behavior is to now list all programs when there is no search query  -->
      <!-- <span v-if="!searchQuery.length && !departmentFilter">
        No search in progress.
      </span> -->
      <span v-if="isLoading">Search in progress</span>
      <span v-else-if="!programs.length">No results</span>
      <span
        v-else
        class="visually-hidden"
      >{{ programs.length }} results found</span>
    </p>
    <div v-if="total">
      <ProgramsTable
        style="margin-bottom: 0 !important"
        :programs="programsFormatted"
      />
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import SearchInput from '@/components/SearchInput'
import DepartmentFilter from '@/components/DepartmentFilter'
import ProgramsTable from '@/themes/cuny/ProgramsTable'
import pageLayout from '@/mixins/pageLayoutMixin'
import usePrograms from '@/composables/usePrograms'

export default {
  components: {
    SearchInput,
    DepartmentFilter,
    ProgramsTable,
  },
  setup(props, { root }) {
    const columns = [
      'name',
      'cipCode',
      'type',
      'degreeDesignation',
      'fieldOfStudy',
      'hegisCode',
      'contacts',
      'transcriptDescription',
    ]
    const {
      updatePage,
      search,
      fetchData,
      programsFormatted,
      hasDepartmentFilter,
      totalPages,
      departmentFilter,
      programs,
      filters,
      total,
      currentPage,
      isLoading,
      searchQuery,
      pageSize,
    } = usePrograms(root, { pageSize: 50, columns })

    return {
      updatePage,
      search,
      fetchData,
      programsFormatted,
      hasDepartmentFilter,
      totalPages,
      departmentFilter,
      programs,
      filters,
      total,
      currentPage,
      isLoading,
      searchQuery,
      pageSize,
    }
  },
  head() {
    return {
      title: `Programs | ${this.catalogWideMetaTitle}`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `Programs | ${this.catalogWideMetaTitle}`,
        },
      ],
    }
  },
  mixins: [pageLayout],
  computed: {
    ...mapGetters('settings', ['catalogWideMetaTitle']),
  },
  watch: {
    'filters.department': 'search',
    '$route.query': 'fetchData',
  },
  mounted() {
    this.fetchData()
  },
}
</script>

<style lang="css" scoped>
.search-wrapper /deep/ .card {
  display: flex !important;
  margin-bottom: 0;
}

.bg-color {
  background-color: white !important;
}

@screen md {
  .programs-list {
    column-count: 2;
  }
}
</style>
